import LiteYouTubeEmbed from "react-lite-youtube-embed";
import { graphql, useFragment, useRefetchableFragment } from "react-relay";
import { Icon } from "@/components/icon";
import { Spinner } from "@/components/ui/spinner";
import type { courseVideo$key } from "./__generated__/courseVideo.graphql";
import "react-lite-youtube-embed/dist/LiteYouTubeEmbed.css";
import Cookies from "js-cookie";
import MediaThemeSutro from "player.style/sutro/react";
import { lazy, Suspense, useEffect, useState } from "react";
import VimeoVideo from "vimeo-video-element/react";
import { useColors } from "@/hooks/useColors";
import type { CourseVideoVideoQuery } from "./__generated__/CourseVideoVideoQuery.graphql";
import type { courseVideo_video$key } from "./__generated__/courseVideo_video.graphql";

const MuxPlayer = lazy(() => import("@/components/lazy-mux-player"));

function getPlaybackRate(value?: number | string) {
	const parsedRate =
		typeof value === "number" ? value : Number.parseFloat(value || "");

	return Number.isFinite(parsedRate) && parsedRate > 0 ? parsedRate : 1;
}

type CourseVideoProps = {
	course: courseVideo$key;
	hideControls?: boolean;
	autoPlay?: boolean;
	onEnded?: () => void;
};

export function CourseVideo({
	course,
	hideControls,
	autoPlay,
	onEnded,
}: CourseVideoProps) {
	const { primary } = useColors();
	const [playbackRate, setPlaybackRate] = useState(() =>
		getPlaybackRate(Cookies.get("COURSE_VIDEO_RATE")),
	);

	const data = useFragment<courseVideo$key>(
		graphql`
    fragment courseVideo on CourseContent {
      video {
        ... on ExternalVideo {
          url
        }
        ... on VideoFile {
          id
          rawID
          muxId
          url
          status
          thumbnail {
            signedUrl
          }
          playback {
            playback
            tokens {
              playback
              storyboard
              thumbnail
            }
          }

          ...courseVideo_video
        }

        __typename
      }
    }
  `,
		course,
	);

	const [videoData, refetch] = useRefetchableFragment<
		CourseVideoVideoQuery,
		courseVideo_video$key
	>(
		graphql`
    fragment courseVideo_video on VideoFile
    @refetchable(queryName: "CourseVideoVideoQuery")
    {
      id
      rawID
      muxId
      url
      status
      playback {
        playback
        tokens {
        playback
        storyboard
        thumbnail
        }
      }
    }
  `,
		data.video?.__typename === "VideoFile" ? data.video : null,
	);

	useEffect(() => {
		if (videoData?.status === "PROCESSING") {
			const timeout = setTimeout(() => {
				refetch({}, { fetchPolicy: "network-only" });
			}, 10000);

			return () => {
				clearTimeout(timeout);
			};
		}
	}, [videoData?.status, refetch]);

	if (data.video?.__typename === "VideoFile") {
		if (data.video?.status === "ERROR") {
			return (
				<div
					className="flex w-full flex-col items-center justify-center gap-3 overflow-x-hidden bg-red-100 font-medium text-red-800 text-xs"
					style={{ aspectRatio: 16 / 9 }}
				>
					<Icon name="CircleAlert" className="size-5" />
					Video Indisponível. Entre em contato com o administrador para mais
					informações.
				</div>
			);
		}

		if (data.video?.status === "PROCESSING") {
			return (
				<div
					className="flex w-full flex-col items-center justify-center gap-3 overflow-x-hidden bg-zinc-950/10 dark:bg-white/10"
					style={{ aspectRatio: 16 / 9 }}
				>
					<Spinner />

					<span className="font-medium text-muted-foreground text-sm">
						Vídeo em processamento. Ele estará disponível em alguns minutos.
					</span>
				</div>
			);
		}

		return (
			<div className="flex w-full items-center justify-center overflow-x-hidden">
				<Suspense
					fallback={
						<div className="aspect-video w-full bg-zinc-950/10 dark:bg-white/10 animate-pulse" />
					}
				>
					<MuxPlayer
						autoPlay={autoPlay}
						streamType="on-demand"
						playbackId={data.video?.playback?.playback}
						tokens={{
							playback: data?.video?.playback?.tokens?.playback || undefined,
							storyboard:
								data?.video?.playback?.tokens?.storyboard || undefined,
							thumbnail: data?.video?.playback?.tokens?.thumbnail || undefined,
						}}
						playbackRate={playbackRate}
						{...(data.video?.thumbnail?.signedUrl
							? { poster: data.video.thumbnail?.signedUrl }
							: {})}
						style={{
							aspectRatio: 16 / 9,
							// @ts-expect-error this parameter works
							"--controls": hideControls ? "none" : undefined,
						}}
						onEnded={onEnded}
						onRateChange={(event) => {
							const nextPlaybackRate = getPlaybackRate(
								// @ts-expect-error mux-player ratechange exposes playbackRate on the current target
								event.currentTarget?.playbackRate,
							);

							setPlaybackRate(nextPlaybackRate);
							Cookies.set("COURSE_VIDEO_RATE", nextPlaybackRate.toString());
						}}
						accentColor={primary}
						secondaryColor="transparent"
					/>
				</Suspense>
			</div>
		);
	}

	if (data.video?.__typename === "ExternalVideo") {
		if (
			data.video.url.includes("youtube.com") ||
			data.video.url.includes("yt.com")
		) {
			const regExp =
				/^.*((youtu.be\/)|(v\/)|(\/u\/\w\/)|(embed\/)|(watch\?))\??v?=?([^#&?]*).*/;

			const match = data.video.url.match(regExp);

			if (!match) {
				return null;
			}

			return (
				<LiteYouTubeEmbed
					autoplay={autoPlay}
					id={match[7]}
					title="Video"
					poster="maxresdefault"
					webp
					onEnd={onEnded}
					params={hideControls ? "controls=0" : undefined}
					lazyLoad
					enableJsApi
					aspectWidth={16}
					aspectHeight={9}
				/>
			);
		}

		if (data.video.url.includes("vimeo.com")) {
			return (
				<MediaThemeSutro className="aspect-video w-full">
					<VimeoVideo slot="media" src={data.video.url} playsInline />
				</MediaThemeSutro>
			);
		}

		return (
			<div className="flex w-full items-center justify-center overflow-x-hidden aspect-video">
				<iframe
					src={data.video.url}
					className="size-full"
					title="Course Title"
				/>
			</div>
		);
	}

	return null;
}
